import { FC, useState } from 'react'
import { Show, TopToolbar, useRecordContext, useTranslate } from 'react-admin'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Collapse from '@mui/material/Collapse'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import SkipNextIcon from '@mui/icons-material/SkipNext'
import { useNavigate } from 'react-router-dom'
import { themeColor, xxllncColor } from '../../layout/theme/color'
import { WorkflowExecutionOut, WorkflowExecutionStepOut } from '../../types/apiRequests'
import AiDoctorPanel from './AiDoctorPanel'

const STEP_ICONS: Record<string, FC<{ sx?: object }>> = {
  success: CheckCircleIcon,
  error: ErrorIcon,
  running: PlayArrowIcon,
  pending: HourglassEmptyIcon,
  skipped: SkipNextIcon,
}

const STEP_COLORS: Record<string, string> = {
  success: xxllncColor.green100,
  error: xxllncColor.red100,
  running: xxllncColor.dark100,
  pending: themeColor.slate40,
  skipped: themeColor.slate40,
}

/* eslint-disable camelcase */
const NODE_TYPE_LABELS: Record<string, string> = {
  trigger: 'Trigger',
  tool_call: 'MCP Tool',
  ai_call: 'AI',
  transform: 'Transform',
  condition: 'Condition',
  visualization: 'Visualization',
  loop: 'Loop',
  merge: 'Merge',
}
/* eslint-enable camelcase */

const ShowActions: FC = () => {
  const navigate = useNavigate()
  const translate = useTranslate()

  return (
    <TopToolbar>
      <Button
        size="small"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/workflow_execution')}
        sx={{ textTransform: 'none' }}
      >
        {translate('workflow.actions.executions')}
      </Button>
    </TopToolbar>
  )
}

type ChipColor = 'success' | 'error' | 'warning' | 'info' | 'default'

interface InfoCardProps {
  label: string
  value: string | undefined
  chip?: boolean
  chipColor?: ChipColor
}

const InfoCard: FC<InfoCardProps> = ({ label, value, chip, chipColor }) => (
  <Box sx={{ minWidth: 140 }}>
    <Typography variant="caption" sx={{ color: themeColor.slate50, display: 'block', mb: 0.5 }}>
      {label}
    </Typography>
    {chip ? (
      <Chip
        label={value}
        size="small"
        color={chipColor ?? 'default'}
        sx={{ height: 24, fontSize: 11, textTransform: 'capitalize' }}
      />
    ) : (
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {value || '-'}
      </Typography>
    )}
  </Box>
)

const JsonViewer: FC<{ data: Record<string, unknown>; label: string }> = ({ data, label }) => {
  const keys = Object.keys(data)
  if (keys.length === 0) return null

  return (
    <Box sx={{ mt: 1 }}>
      <Typography
        variant="caption"
        sx={{ color: themeColor.slate50, fontWeight: 600, display: 'block', mb: 0.5 }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          bgcolor: themeColor.slate05,
          borderRadius: '8px',
          border: `1px solid ${themeColor.slate10}`,
          p: 1.5,
          maxHeight: 400,
          overflow: 'auto',
        }}
      >
        <Typography
          component="pre"
          sx={{
            fontFamily: '"SF Mono", "Fira Code", "Consolas", monospace',
            fontSize: 11,
            lineHeight: 1.6,
            color: themeColor.slate80,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            m: 0,
          }}
        >
          {JSON.stringify(data, null, 2)}
        </Typography>
      </Box>
    </Box>
  )
}

const StepCard: FC<{
  step: WorkflowExecutionStepOut;
  index: number;
  defaultExpanded?: boolean;
}> = ({ step, index, defaultExpanded = false }) => {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const translate = useTranslate()

  // eslint-disable-next-line security/detect-object-injection
  const Icon = STEP_ICONS[step.status] ?? HourglassEmptyIcon
  // eslint-disable-next-line security/detect-object-injection
  const color = STEP_COLORS[step.status] ?? themeColor.slate40
  const displayName = step.node_label || step.node_id
  // eslint-disable-next-line security/detect-object-injection
  const typeLabel = NODE_TYPE_LABELS[step.node_type] ?? step.node_type

  const hasData =
    (step.output_data && Object.keys(step.output_data).length > 0) ||
    (step.input_data && Object.keys(step.input_data).length > 0)

  return (
    <Paper
      variant="outlined"
      sx={{
        'borderRadius': '12px',
        'borderLeft': `4px solid ${color}`,
        'overflow': 'hidden',
        'cursor': hasData ? 'pointer' : 'default',
        'transition': 'box-shadow 0.15s ease',
        '&:hover': hasData ? { boxShadow: '0 1px 4px rgba(0,0,0,0.08)' } : {},
      }}
      onClick={() => hasData && setExpanded((v) => !v)}
    >
      <Box sx={{ p: 2, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
          <Typography
            variant="caption"
            sx={{ color: themeColor.slate40, fontWeight: 600, fontSize: 10, minWidth: 14, textAlign: 'right' }}
          >
            {index + 1}
          </Typography>
          <Icon sx={{ color, fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {displayName}
            </Typography>
            <Chip label={typeLabel} size="small" sx={{ height: 20, fontSize: 10 }} />
            <Chip
              label={translate(`workflow.execution.${step.status}`)}
              size="small"
              color={
                step.status === 'success'
                  ? 'success'
                  : step.status === 'error'
                    ? 'error'
                    : 'default'
              }
              variant="outlined"
              sx={{ height: 20, fontSize: 10 }}
            />
            {step.duration_ms != null && (
              <Typography variant="caption" sx={{ color: themeColor.slate50 }}>
                {step.duration_ms < 1000
                  ? `${step.duration_ms}ms`
                  : `${(step.duration_ms / 1000).toFixed(1)}s`}
              </Typography>
            )}
            <Box sx={{ flex: 1 }} />
            {hasData && (
              expanded
                ? <KeyboardArrowDownIcon sx={{ fontSize: 18, color: themeColor.slate40 }} />
                : <KeyboardArrowRightIcon sx={{ fontSize: 18, color: themeColor.slate40 }} />
            )}
          </Box>
          {step.error && (
            <Typography variant="caption" sx={{ color: xxllncColor.red100, display: 'block' }}>
              {step.error}
            </Typography>
          )}
          {!expanded && step.output_data && Object.keys(step.output_data).length > 0 && step.status === 'success' && (
            <Typography
              variant="caption"
              sx={{
                color: themeColor.slate50,
                display: 'block',
                mt: 0.5,
                fontFamily: '"SF Mono", "Fira Code", monospace',
                fontSize: 11,
                maxHeight: 40,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {JSON.stringify(step.output_data).substring(0, 200)}
            </Typography>
          )}
        </Box>
      </Box>
      <Collapse in={expanded}>
        <Box
          sx={{ px: 2, pb: 2, pt: 0, ml: 5.5 }}
          onClick={(e) => e.stopPropagation()}
        >
          {step.input_data && Object.keys(step.input_data).length > 0 && (
            <JsonViewer data={step.input_data} label="Input" />
          )}
          {step.output_data && Object.keys(step.output_data).length > 0 && (
            <JsonViewer data={step.output_data} label="Output" />
          )}
        </Box>
      </Collapse>
    </Paper>
  )
}

const StepTimeline: FC = () => {
  const record = useRecordContext<WorkflowExecutionOut>()
  const translate = useTranslate()
  if (!record?.steps?.length) return null

  const sortedSteps = [...record.steps].sort((a, b) => {
    const dateA = a.created ? new Date(a.created).getTime() : 0
    const dateB = b.created ? new Date(b.created).getTime() : 0
    return dateA - dateB
  })

  const autoExpandIndexes = new Set<number>()
  sortedSteps.forEach((step, idx) => {
    if (step.status === 'error') {
      autoExpandIndexes.add(idx)
      if (idx > 0) autoExpandIndexes.add(idx - 1)
    }
  })

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
        {translate('workflow.execution.steps')}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {sortedSteps.map((step: WorkflowExecutionStepOut, idx: number) => (
          <StepCard
            key={`${step.node_id}-${step.attempt}`}
            step={step}
            index={idx}
            defaultExpanded={autoExpandIndexes.has(idx)}
          />
        ))}
      </Box>
    </Box>
  )
}

const ExecutionDetail: FC = () => {
  const record = useRecordContext<WorkflowExecutionOut>()
  const translate = useTranslate()
  if (!record) return null

  const statusColor: ChipColor =
    record.status === 'completed'
      ? 'success'
      : record.status === 'failed'
        ? 'error'
        : record.status === 'running'
          ? 'info'
          : 'default'

  const duration =
    record.started_at && record.finished_at
      ? Math.round(
        (new Date(record.finished_at).getTime() - new Date(record.started_at).getTime()) / 1000,
      )
      : null

  return (
    <Box sx={{ p: 2, maxWidth: 900 }}>
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '12px', mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          <InfoCard
            label={translate('workflow.execution.workflow')}
            value={record.workflow_display_name}
          />
          <InfoCard
            label={translate('workflow.execution.organization')}
            value={record.organization_display_name}
          />
          <InfoCard
            label={translate('workflow.execution.trigger')}
            value={record.trigger_type}
          />
          <InfoCard
            label={translate('workflow.execution.triggeredBy')}
            value={String(record.triggered_by || '')}
          />
          <InfoCard
            label={translate('workflow.execution.status')}
            value={translate(`workflow.execution.${record.status}`)}
            chip
            chipColor={statusColor}
          />
          {duration != null && (
            <InfoCard
              label={translate('workflow.execution.duration')}
              value={duration < 60 ? `${duration}s` : `${Math.floor(duration / 60)}m ${duration % 60}s`}
            />
          )}
          <InfoCard
            label={translate('workflow.execution.stepsCount')}
            value={`${record.steps?.filter((s) => s.status === 'success').length ?? 0}/${record.steps?.length ?? 0}`}
          />
        </Box>
      </Paper>

      {record.error && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: xxllncColor.opRed10, borderRadius: '12px' }}>
          <Typography variant="caption" sx={{ color: xxllncColor.red100, fontWeight: 600 }}>
            {translate('workflow.execution.error')}
          </Typography>
          <Typography variant="body2" sx={{ color: xxllncColor.red100 }}>
            {record.error}
          </Typography>
        </Paper>
      )}

      <AiDoctorPanel />

      {record.trigger_input && Object.keys(record.trigger_input).length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: '12px' }}>
          <Typography
            variant="caption"
            sx={{ color: themeColor.slate50, fontWeight: 600, display: 'block', mb: 1 }}
          >
            Trigger Input
          </Typography>
          <Box
            sx={{
              bgcolor: themeColor.slate05,
              borderRadius: '8px',
              border: `1px solid ${themeColor.slate10}`,
              p: 1.5,
            }}
          >
            <Typography
              component="pre"
              sx={{
                fontFamily: '"SF Mono", "Fira Code", "Consolas", monospace',
                fontSize: 11,
                lineHeight: 1.6,
                color: themeColor.slate80,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                m: 0,
              }}
            >
              {JSON.stringify(record.trigger_input, null, 2)}
            </Typography>
          </Box>
        </Paper>
      )}

      <StepTimeline />
    </Box>
  )
}

const WorkflowExecutionShow: FC = () => (
  <Show actions={<ShowActions />}>
    <ExecutionDetail />
  </Show>
)

export default WorkflowExecutionShow
